/**
 * A dashboard value as the reader reads it.
 *
 * The engine sends each value as an exact decimal string, already rounded to
 * the widget's own places, so this only groups the digits (lakh/crore, the
 * way every NEPSE screen here does) and puts the unit beside them. It never
 * rounds again: `formatMoney` fixes two places, and a widget the reader set
 * to four would lose two of them on the way to the screen.
 */
import { cmp, money, toChartNumber } from '@/lib/money'
import type {
  BreakdownPart,
  CheckWarning,
  Coverage,
  Drawn,
  Point,
  ScopeRows,
  WidgetUnit,
  Worked,
} from '@/features/mitra/api/dashboard'

/** `en-IN`, for lakh grouping (see `lib/format.ts` on why not `en-NP`). */
const GROUP = new Intl.NumberFormat('en-IN')

const MINUS = '−'

/** The engine's digits, lakh-grouped, with the typographic minus. */
export function groupDigits(value: string): string {
  const negative = value.startsWith('-')
  const body = negative ? value.slice(1) : value
  const [whole = '0', fraction] = body.split('.')
  const grouped = GROUP.format(BigInt(whole === '' ? '0' : whole))
  return `${negative ? MINUS : ''}${grouped}${fraction !== undefined ? `.${fraction}` : ''}`
}

/** A value with its unit, or a dash for one there is none of: never a zero. */
export function shownValue(value: string | null | undefined, unit: WidgetUnit): string {
  if (value === null || value === undefined) return '—'
  const digits = groupDigits(value)
  switch (unit) {
    case 'NPR':
      return digits.startsWith(MINUS) ? `${MINUS}Rs ${digits.slice(1)}` : `Rs ${digits}`
    case 'percent':
      return `${digits}%`
    case 'shares':
      return `${digits} kitta`
    default:
      return digits
  }
}

/**
 * Why a value is a dash, or what an aggregate left out, in a sentence. A name
 * (`eps`) is a figure nobody filed; a reason in words ("division by zero") is
 * arithmetic with no answer, and is said differently, since "No division by
 * zero on record" would say the opposite of what happened.
 */
export function gapText(worked: Partial<Worked>): string | null {
  const parts: string[] = []
  if ((worked.value === null || worked.value === undefined) && worked.missing?.length) {
    const names = worked.missing.filter((m) => !m.includes(' '))
    const reasons = worked.missing.filter((m) => m.includes(' '))
    if (names.length) parts.push(`No ${names.join(', ')} on record.`)
    if (reasons.length) parts.push(`Cannot be worked out: ${reasons.join(', ')}.`)
  }
  if (worked.left_out && worked.left_out > 0) {
    const names = (worked.left_out_for ?? []).join(', ')
    parts.push(
      worked.left_out === 1
        ? `Leaves out one holding with no ${names}.`
        : `Leaves out ${String(worked.left_out)} holdings with no ${names}.`,
    )
  }
  return parts.length ? parts.join(' ') : null
}

/** The reasons a widget's points have no value, said once under it. */
export function gapsOf(points: readonly Point[]): string | null {
  const texts = [...new Set(points.map((p) => gapText(p)).filter((t): t is string => t !== null))]
  return texts.length ? texts.join(' ') : null
}

/**
 * How long a bar is, as a share of the longest in its widget. Drawing only,
 * never printed: the digits beside the bar are the engine's.
 */
export function barShare(value: string | null, values: readonly (string | null)[]): number {
  if (value === null) return 0
  const size = (v: string): number => Math.abs(toChartNumber(money(v)))
  const largest = Math.max(0, ...values.map((v) => (v === null ? 0 : size(v))))
  return largest > 0 ? size(value) / largest : 0
}

/** Two of the engine's decimal strings in order, null last, for sorting a column. */
export function compareValues(a: string | null, b: string | null): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1
  return cmp(money(a), money(b))
}

/** A drawn widget's value in a line: the figure, or its first few points. */
export function valueLine(widget: Drawn, points = 2): string {
  if (widget.error || widget.reason) return '—'
  // A series: each line's last step, the engine's own figure, never a move worked out here.
  if (widget.series) {
    const lines = widget.series.slice(0, points).map((line) => {
      let last: string | null = null
      for (const p of line.points) if (p.value !== null) last = p.value
      const shown = shownValue(last, widget.unit)
      return widget.kind === 'number' ? shown : `${line.label} ${shown}`
    })
    return lines.join(' · ') + (widget.series.length > points ? ' …' : '')
  }
  if (widget.kind === 'number') return shownValue(widget.value ?? null, widget.unit)
  const list = (widget.points ?? [])
    .slice(0, points)
    .map((p) => `${p.label} ${shownValue(p.value, widget.unit)}`)
  const more = (widget.points?.length ?? 0) > points ? ' …' : ''
  return list.join(' · ') + more
}

/** How many of the rows a value stands on, as "51/60". */
export function coverageText(coverage: Coverage): string {
  return `${String(coverage.counted)}/${String(coverage.total)}`
}

/**
 * The same figure in words, so a reader never has to decode "52/57": every row
 * counted reads "All 57 holdings", otherwise "52 of 57 holdings". `noun` is
 * what the rows are; without one the words stop at the numbers.
 */
export function coverageWords(coverage: Coverage, noun = ''): string {
  const tail = noun ? ` ${noun}` : ''
  return coverage.counted >= coverage.total
    ? `All ${String(coverage.total)}${tail}`
    : `${String(coverage.counted)} of ${String(coverage.total)}${tail}`
}

/**
 * Said in words where a figure stands on only some of its rows: what the rest
 * lack ("Left out: no Cash div/share"). The ring beside the figure already
 * says how many ("52 of 57"), so the count is not said twice. The reasons are
 * the engine's own field names, shown by the labels its picture gives them.
 * "Left out" alone where the engine names no reason; null where every row counts.
 */
export function partialNote(
  coverage: Coverage | null | undefined,
  leftOutFor: readonly string[],
  labels: ReadonlyMap<string, string>,
): string | null {
  if (!coverage || coverage.total === 0 || coverage.counted >= coverage.total) return null
  const why = [...new Set(leftOutFor.map((name) => labels.get(name) ?? name))]
  return why.length > 0 ? `Left out: no ${why.join(' or ')}` : 'Left out'
}

/** One company's part of a total, as the expanded card's bars draw it. */
export interface Part {
  label: string
  value: string | null
  counted: boolean
  /** The bar's length against the largest part: drawing only. */
  share: number
}

/**
 * One total of a single figure, company by company: the counted parts
 * largest first (top `top`, with how many more), then the companies it left
 * out, greyed. From the engine's breakdown; nothing is added up here.
 */
export function contributionsOf(
  part: BreakdownPart,
  top = 8,
): { parts: Part[]; more: number; leftOut: Part[] } {
  const rows = part.rows
  const counted = rows.filter((r) => r.counted && r.value !== null)
  const values = counted.map((r) => r.value)
  const ordered = [...counted].sort((a, b) => compareValues(absolute(b.value), absolute(a.value)))
  const toPart = (r: (typeof rows)[number]): Part => ({
    label: r.label,
    value: r.value,
    counted: r.counted,
    share: r.counted ? barShare(r.value, values) : 0,
  })
  return {
    parts: ordered.slice(0, top).map(toPart),
    more: Math.max(0, ordered.length - top),
    leftOut: rows.filter((r) => !r.counted).map(toPart),
  }
}

/**
 * A breakdown amount kept short by MOVING the engine's digits, never by
 * dividing: eight or more whole digits read in crore ("1.58 Cr"), six or
 * more in lakh ("33.29 L"), anything shorter in full, lakh-grouped. The
 * places shown are the engine's own digits, cut, never rounded.
 */
export function compactDigits(value: string): string {
  const negative = value.startsWith('-')
  const whole = (negative ? value.slice(1) : value).split('.')[0] ?? ''
  const sign = negative ? MINUS : ''
  const moved = (places: number, suffix: string) =>
    `${sign}${groupDigits(whole.slice(0, -places))}.${whole.slice(-places, -places + 2)} ${suffix}`
  if (whole.length >= 8) return moved(7, 'Cr')
  if (whole.length >= 6) return moved(5, 'L')
  return groupDigits(value)
}

const absolute = (value: string | null): string | null =>
  value?.startsWith('-') ? value.slice(1) : value

/** The two numbers a warning's chip shows: "23.50 → 24.85". */
export function warningPair(warning: CheckWarning): string | null {
  if (!warning.compare) return null
  const side = (v: string | null) => (v === null ? '—' : groupDigits(v))
  return `${side(warning.compare.yours)} → ${side(warning.compare.better)}`
}

/** How many rows a long table or list shows a page (owner, 2026-10-18). */
export const PAGE_SIZE = 10

/** What a scope's rows are called, for the coverage meter's words. */
export function nounOf(scope: ScopeRows['kind'] | undefined): string {
  if (scope === 'brokers') return 'brokers'
  return scope === undefined || scope === 'book' ? 'holdings' : 'companies'
}
